import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  DollarSign, 
  AlertTriangle,
  Calendar,
  User,
  Weight,
  FileText
} from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardHeader, CardContent, Skeleton } from '@components/Card';
import { PermissionGuard } from '@components/PermissionGuard';
import { rawMaterialService } from '@services/rawMaterialService';
import { RawMaterial, MATERIAL_TYPE_OPTIONS, UNIT_OPTIONS } from '@/types/rawMaterial';

/**
 * Página de detalhes da matéria-prima
 * Exibe todas as informações em formato somente leitura
 */
export const RawMaterialDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [material, setMaterial] = useState<RawMaterial | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadMaterial(id);
    }
  }, [id]);

  const loadMaterial = async (materialId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const materialData = await rawMaterialService.getRawMaterialById(parseInt(materialId));
      setMaterial(materialData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar matéria-prima');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Data inválida';
    }
  };

  const getMaterialTypeLabel = (type: number) => {
    const option = MATERIAL_TYPE_OPTIONS.find(o => o.value === type);
    return option ? option.label : 'Desconhecido';
  };

  const getMaterialTypeDescription = (type: number) => {
    const option = MATERIAL_TYPE_OPTIONS.find(o => o.value === type);
    return option ? option.description : '';
  };

  const getUnitLabel = (unit?: string) => {
    if (!unit) return '-';
    const option = UNIT_OPTIONS.find(o => o.value === unit);
    return option ? option.label : unit;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/raw-materials')}>
            <ArrowLeft size={20} />
          </Button>
          <div className="flex-1">
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
        
        <div className="grid gap-6">
          <Card>
            <CardContent>
              <Skeleton className="h-40" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/raw-materials')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-h1">Matéria-prima não encontrada</h1>
          </div>
        </div>
        
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <div className="text-center py-8">
              <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={() => id && loadMaterial(id)} variant="outline">
                Tentar novamente
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!material) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/raw-materials')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-h1">{material.description || 'Material sem nome'}</h1>
            <p className="text-slate-600">Código: {material.code || 'N/A'}</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <PermissionGuard permission="raw_materials.write">
            <Button
              variant="outline"
              icon={<Edit size={20} />}
              onClick={() => navigate(`/raw-materials/${material.id}/edit`)}
            >
              Editar
            </Button>
          </PermissionGuard>
        </div>
      </div>

      {/* Painéis de informação */}
      <div className="grid gap-6">
        
        {/* Informações Básicas */}
        <Card>
          <CardHeader title="Informações Básicas" />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-small text-slate-500 mb-1">Código</p>
                <p className="font-medium">{material.code || '-'}</p>
              </div>
              
              <div>
                <p className="text-small text-slate-500 mb-1">NCM</p>
                <p className="font-medium font-mono">{material.ncm || '-'}</p>
              </div>

              <div className="md:col-span-2">
                <p className="text-small text-slate-500 mb-1">Descrição</p>
                <p className="font-medium leading-relaxed">{material.description || '-'}</p>
              </div>

              <div>
                <p className="text-small text-slate-500 mb-1">Tipo de Material</p>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    material.material_type === 0 
                      ? 'bg-purple-100 text-purple-800' 
                      : 'bg-cyan-100 text-cyan-800'
                  }`}>
                    {getMaterialTypeLabel(material.material_type)}
                  </span>
                  <span className="text-sm text-slate-500">{getMaterialTypeDescription(material.material_type)}</span>
                </div>
              </div>

              <div>
                <p className="text-small text-slate-500 mb-1">Unidade de Medida</p>
                <p className="font-medium">{getUnitLabel(material.unit)}</p>
              </div>

              <div>
                <p className="text-small text-slate-500 mb-1">Status</p>
                <span className={`px-2 py-1 rounded text-sm ${material.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {material.is_active ? 'Ativo' : 'Inativo'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Custo e Peso */}
        <Card>
          <CardHeader title="Custo e Peso" />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <DollarSign className="text-green-600" size={20} />
                <div>
                  <p className="text-small text-slate-500">Custo Unitário</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(material.cost || 0)}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Weight className="text-slate-400" size={20} />
                <div>
                  <p className="text-small text-slate-500">Peso Bruto</p>
                  <p className="text-xl font-medium">{material.total_weight || 0} kg</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Controle de Estoque */}
        <Card>
          <CardHeader title="Controle de Estoque" />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-small text-slate-500 mb-1">Estoque Total</p>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold text-slate-900">{material.stock_quantity ?? 0}</p>
                  <span className="text-sm text-slate-500">{material.unit || 'UN'}</span>
                </div>
              </div>
              
              <div>
                <p className="text-small text-slate-500 mb-1">Estoque Disponível</p>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold text-green-600">{material.stock_quantity_available ?? 0}</p>
                  <span className={`text-sm px-2 py-1 rounded ${
                    (material.stock_quantity_available ?? 0) === 0 
                      ? 'bg-red-100 text-red-800' 
                      : (material.stock_quantity_available ?? 0) <= (material.min_stock_quantity ?? 0)
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-green-100 text-green-800'
                  }`}>
                    {(material.stock_quantity_available ?? 0) === 0 
                      ? 'Sem Estoque' 
                      : (material.stock_quantity_available ?? 0) <= (material.min_stock_quantity ?? 0)
                        ? 'Estoque Baixo'
                        : 'Disponível'}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="mt-4 grid grid-cols-2 gap-4 pt-4 border-t border-slate-200">
              <div>
                <p className="text-small text-slate-500 mb-1">Estoque Mínimo</p>
                <p className="font-medium">{material.min_stock_quantity ?? 0}</p>
              </div>
              <div>
                <p className="text-small text-slate-500 mb-1">Estoque Máximo</p>
                <p className="font-medium">{material.max_stock_quantity ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Observações */}
        {material.notes && (
          <Card>
            <CardHeader title="Observações" />
            <CardContent>
              <div className="flex items-start gap-3">
                <FileText className="text-slate-400 mt-0.5" size={20} />
                <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{material.notes}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Informações de Sistema */}
        <Card>
          <CardHeader title="Informações de Sistema" />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-3">Criação</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="text-slate-400" size={16} />
                    <span className="text-sm text-slate-600">
                      {material.created_at ? formatDate(material.created_at) : 'N/A'}
                    </span>
                  </div>
                  {material.created_by && (
                    <div className="flex items-center gap-2">
                      <User className="text-slate-400" size={16} />
                      <span className="text-sm text-slate-600">{material.created_by}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-3">Última Modificação</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="text-slate-400" size={16} />
                    <span className="text-sm text-slate-600">
                      {material.updated_at ? formatDate(material.updated_at) : 'N/A'}
                    </span>
                  </div>
                  {material.last_updated_by && (
                    <div className="flex items-center gap-2">
                      <User className="text-slate-400" size={16} />
                      <span className="text-sm text-slate-600">{material.last_updated_by}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
